/**
 * Collection Products Snippet
 * Handles grid switching, product type filtering, advanced storefront
 * filters (collection.filters) with fetch + section-render swap, sort,
 * and entrance animations.
 *
 * Used in: collection page, designer page, and anywhere products are listed.
 */

import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import DropDown from './dropdown';
import { reinitInfiniteScroll } from '../utils/infinite-scroll.js';
import { lockScroll, unlockScroll } from '../utils/scroll-lock.js';
import { initFilterChips, collapseSingleValueParams } from '../utils/filter-chips.js';

gsap.registerPlugin(ScrollTrigger);

export default class CollectionProducts {
  constructor(container) {
    this.container = container;
    this.sectionId =
      container.closest('[data-section-id]')?.dataset.sectionId ||
      container.dataset.sectionId ||
      'collection';

    this.refreshElementRefs();

    // Type chips are exclusive — one type at a time. Installed once on
    // document (capture phase) by the shared util, so it applies to every
    // chip on the page whatever section owns it, runs before the per-chip
    // change handlers, and survives grid/filter DOM swaps.
    initFilterChips();

    this.currentGrid = 3;
    this.modalOpen = false;
    this.filterFetchAbort = null;
    this.mm = gsap.matchMedia();
    this.batchTriggers = [];

    this.init();
    this.events();

    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('keydown', this.onKeydown);

    this.container.addEventListener('infinite-scroll:before-append', this.onBeforeAppend);
    this.container.addEventListener('infinite-scroll:append', this.onInfiniteAppend);
  }

  // ────────────────────────────────────────────────────────────────────
  // Element refs
  // ────────────────────────────────────────────────────────────────────
  refreshElementRefs() {
    this.gridButtons = this.container.querySelectorAll('.collection--controls--grid-buttons button');
    this.productsGrid = this.container.querySelector('.collection-products');
    this.productCards = this.getAnimatableCards();

    // Inline product-type chips (separate from the advanced modal)
    this.typeFilterCheckboxes = this.container.querySelectorAll('.type-filter input[name="product_type"]');
    this.clearTypeFiltersBtn = this.container.querySelector('.clear-type-filters');
    this.resultsCount = this.container.querySelector('.results-count');

    // Advanced filter modal (Shopify collection.filters)
    this.filterByBtn = this.container.querySelector('button.filterby');
    this.filterCountEl = this.filterByBtn?.querySelector('[data-filter-count]') || null;
    const modalInContainer = this.container.querySelector('[data-filter-modal]');
    const modalInBody = document.body.querySelector('[data-filter-modal]');
    this.filterModal = modalInContainer || modalInBody || this.filterModal || null;

    if (this.filterModal && this.filterModal.parentElement !== document.body) {
      document.body.appendChild(this.filterModal);
    }
    this.filterModalClose = this.filterModal?.querySelector('.filters--header .close');
    this.filterInputs = this.filterModal?.querySelectorAll('input[data-filter-url-add]') || [];
    this.priceInputs = this.filterModal?.querySelectorAll('input[data-filter-price]') || [];
    this.clearFiltersBtn = this.filterModal?.querySelector('.clear[data-filter-url-clear]');
    this.applyFilterBtn = this.filterModal?.querySelector('.filters--content--footer .submit');
    this.selectedFiltersWrap = this.container.querySelector('[data-selected-filters]');
    this.selectedFiltersContainer = this.selectedFiltersWrap?.querySelector('.selected-filters-container');
    this.selectedFiltersClear = this.selectedFiltersWrap?.querySelector('.clear[data-filter-url-clear]');

    // Sort
    this.sortbySelect = this.container.querySelector('.sortby-select');


  }

  // ────────────────────────────────────────────────────────────────────
  // Init
  // ────────────────────────────────────────────────────────────────────
  init = () => {
    if (this.sortbySelect) {
      new DropDown('.sortby-select', {
        button: '.sortby-display',
        pane: '.sortby-options',
        options: '.sortby-options li',
        isOpen: false,
        clickOutside: true,
      });
    }

    this.initFilterDropdowns();
    this.collapseSelectedFiltersIfEmpty();

    if (this.productCards.length > 0) {
      this.runAnim();
    }
  };

  initFilterDropdowns = () => {
    this.filterModal?.querySelectorAll('.filters--content--item').forEach((element) => {
      new DropDown('.filters--content--item', {
        button: element.querySelector('.filters--content--item-button'),
        pane: element.querySelector('.filters--content--item-options-pane'),
        isOpen: true,
        clickOutside: false,
      });
    });
  };

  collapseSelectedFiltersIfEmpty = () => {
    if (!this.selectedFiltersWrap) return;
    const hasChips = (this.selectedFiltersContainer?.children.length || 0) > 0;
    gsap.set(this.selectedFiltersWrap, { height: hasChips ? 'auto' : 0 });
  };

  /**
   * After a filter swap the new grid arrives with the default data-grid
   * baked into Liquid — re-apply the current grid so responsive choices
   * survive the swap.
   */
  restoreGridState = () => {
    if (this.productsGrid) {
      this.productsGrid.dataset.grid = this.currentGrid;
    }
    this.gridButtons.forEach((btn) => {
      btn.classList.toggle('active', parseInt(btn.dataset.grid, 10) === this.currentGrid);
    });
  };

  getProductWrappers = () =>
    this.container.querySelectorAll('.collection-products .product-wrapper');

  getAnimatableCards = () =>
    this.container.querySelectorAll(
      '.collection-products .product-wrapper:not([hidden]) .product-card:not([data-animation-ended="true"])'
    );

  getTypeFilterCheckboxes = () =>
    this.container.querySelectorAll('.type-filter input[name="product_type"]');

  // Server-side type filtering. Liquid sets data-type-filter-param on the chip
  // row when the store's Product type filter exists and the page is a
  // collection URL (storefront filters only apply there). A chip then applies
  // the filter through applyFilter, so the grid, count and pagination come
  // from Shopify already narrowed to that type. Without the param (entity
  // pages) chips fall back to hiding / revealing loaded cards.
  getTypeFilterParam = () =>
    this.container.querySelector('.type-filters')?.dataset.typeFilterParam || '';

  usesServerTypeFilter = () => this.getTypeFilterParam() !== '';

  typeFilterUrl = (label) => {
    const param = this.getTypeFilterParam();
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    url.searchParams.delete('page');
    if (label) url.searchParams.set(param, label);
    return url.toString();
  };

  // Chip state follows the URL (after a fetch, back/forward navigation).
  syncTypeChipsFromUrl = (url) => {
    const param = this.getTypeFilterParam();
    if (!param) return;
    const active = new URL(url, window.location.origin).searchParams.get(param) || '';
    this.getTypeFilterCheckboxes().forEach((cb) => {
      cb.checked = active !== '' && cb.dataset.typeLabel === active;
    });
  };

  onTypeChipChange = (e) => {
    if (!this.usesServerTypeFilter()) {
      this.filterByType();
      return;
    }
    const cb = e.currentTarget;
    this.applyFilter(this.typeFilterUrl(cb.checked ? cb.dataset.typeLabel : ''));
  };

  getSelectedTypes = () =>
    Array.from(this.getTypeFilterCheckboxes())
      .filter((cb) => cb.checked)
      .map((cb) => (cb.value || '').toLowerCase().trim())
      .filter(Boolean);

  wrapperMatchesType = (wrapper, selectedTypes) => {
    if (selectedTypes.length === 0) return true;
    const type = (wrapper.dataset.productType || '').toLowerCase().trim();
    return type !== '' && selectedTypes.includes(type);
  };

  applyTypeVisibility = (wrapper, selectedTypes) => {
    const isVisible = this.wrapperMatchesType(wrapper, selectedTypes);
    wrapper.classList.toggle('is-type-match', isVisible);
    wrapper.hidden = !isVisible;
    return isVisible;
  };

  fillFilteredGrid = () => {
    const wrap = this.container.querySelector('[data-infinite-scroll]');
    wrap?._infiniteScroll?.check();
  };

  runAnim = () => {
    this.productCards = this.getAnimatableCards();

    if (this.productCards.length === 0) return;

    if (this.batchTriggers.length > 0) {
      this.batchTriggers.forEach((trigger) => trigger.kill(true));
      this.batchTriggers = [];
    }

    const visibleCards = Array.from(this.productCards).filter((card) => {
      const wrapper = card.closest('.product-wrapper');
      return wrapper && !wrapper.hidden && wrapper.style.display !== 'none';
    });

    if (visibleCards.length === 0) return;

    gsap.set(visibleCards, { opacity: 0, yPercent: 40, overwrite: true });

    this.batchTriggers = ScrollTrigger.batch(visibleCards, {
      interval: 0.2,
      batchMax: this.currentGrid,
      start: 'top-=40px bottom',
      toggleActions: 'play pause resume reset',
      onEnter: (elements) => {
        elements.forEach(element => {
          element.setAttribute('data-animation-ended', 'true')
        });
        gsap.to(elements, {
          opacity: 1,
          yPercent: 0,
          duration: 1.6,
          delay: 0,
          stagger: { each: 0.1, from: 'center' },
          overwrite: true,
          ease: 'expo.out',
        });
      },
    });
  };

  // ────────────────────────────────────────────────────────────────────
  // Events
  // ────────────────────────────────────────────────────────────────────
  events = () => {
    this.gridButtons.forEach((button) => button.addEventListener('click', this.onClickGridButton));

    // Inline product-type chips (client-side hide/show)
    this.getTypeFilterCheckboxes().forEach((cb) => cb.addEventListener('change', this.onTypeChipChange));
    this.clearTypeFiltersBtn?.addEventListener('click', this.onClickClearTypeFilters);
    this.updateClearTypeFiltersVisibility();

    // Advanced filter modal — filterByBtn stays outside the modal so its
    // listener only needs to bind once; everything inside the modal is
    // rebound in bindFilterControls() after each swap.
    this.filterByBtn?.addEventListener('click', this.toggleFilterModal);

    // Chip removal is delegated on the stable container so it survives the
    // selected-filters region being swapped on every filter change — no need
    // to rebind each X button.
    this.container.addEventListener('click', this.onSelectedFiltersClick);

    this.bindFilterControls();

    // Responsive grid
    this.mm.add('(max-width: 1024px)', () => {
      const btn = this.container.querySelector('.collection--controls--grid-buttons button[data-grid="2"]');
      if (btn) this.onClickGridButton({ currentTarget: btn });
    });
    this.mm.add('(min-width: 1025px)', () => {
      const btn = this.container.querySelector('.collection--controls--grid-buttons button[data-grid="3"]');
      if (btn) this.onClickGridButton({ currentTarget: btn });
    });
  };

  /**
   * (Re)bind event listeners on filter controls — call after a swap so
   * the new DOM nodes are wired up.
   *
   * Inside the modal, input/price changes auto-apply immediately while
   * keeping the modal open. The Apply button still commits and closes,
   * and the outside chip strip and its Clear button apply immediately.
   */
  bindFilterControls = () => {
    this.applyFilterBtn?.addEventListener('click', this.onClickApply);
    this.clearFiltersBtn?.addEventListener('click', this.onClickClearAllFiltersLocal);

    // Auto-apply when a filter option or price range changes.
    this.filterInputs.forEach((input) => input.addEventListener('change', this.onFilterInputChange));
    this.priceInputs.forEach((input) => input.addEventListener('change', this.onFilterInputChange));

    // Modal close button and backdrop click
    this.filterModalClose?.addEventListener('click', this.toggleFilterModal);
    this.filterModal?.addEventListener('click', this.onClickModalBackdrop);

    this.bindSelectedFilters();
  };

  /**
   * Bind the outside selected-filter chip strip. This region always swaps
   * (even when the modal panel is preserved), so it is rebound on its own.
   */
  bindSelectedFilters = () => {
    this.selectedFiltersClear?.addEventListener('click', this.onClickClearAllFilters);
    // Chip X removal is handled by the delegated onSelectedFiltersClick on the
    // stable container, so individual X buttons are not bound here.
  };

  // Delegated on this.container: only the chip's X button removes its filter.
  onSelectedFiltersClick = (e) => {
    const removeBtn = e.target.closest('.selected-filter-remove[data-filter-url-remove]');
    if (!removeBtn) return;
    const url = removeBtn.dataset.filterUrlRemove;
    if (url) this.applyFilter(url);
  };

  // ────────────────────────────────────────────────────────────────────
  // Grid switching
  // ────────────────────────────────────────────────────────────────────
  onClickGridButton = (e) => {
    const target = e.currentTarget;
    const grid = target.dataset.grid;

    this.gridButtons.forEach((btn) => btn.classList.remove('active'));
    target.classList.add('active');

    if (this.productsGrid) {
      this.productsGrid.dataset.grid = grid;
      this.currentGrid = parseInt(grid, 10);
    }

    ScrollTrigger.refresh();
    this.runAnim();
  };

  // ────────────────────────────────────────────────────────────────────
  // Inline product-type chips
  // ────────────────────────────────────────────────────────────────────
  filterByType = () => {
    if (this.getTypeFilterCheckboxes().length === 0) {
      this.runAnim();
      return;
    }

    if (this.usesServerTypeFilter()) {
      // The grid already holds only the selected type and the count came
      // with the fetched section - nothing to hide or recount here.
      this.productsGrid?.classList.remove('is-type-filtered');
      this.updateClearTypeFiltersVisibility();
      ScrollTrigger.refresh();
      this.runAnim();
      return;
    }

    const selectedTypes = this.getSelectedTypes();
    const isFiltered = selectedTypes.length > 0;
    let visibleCount = 0;

    this.productsGrid?.classList.toggle('is-type-filtered', isFiltered);

    this.getProductWrappers().forEach((wrapper) => {
      if (this.applyTypeVisibility(wrapper, selectedTypes)) visibleCount += 1;
    });

    if (this.resultsCount) this.resultsCount.textContent = visibleCount;
    this.updateClearTypeFiltersVisibility();

    ScrollTrigger.refresh();
    this.runAnim();
    this.fillFilteredGrid();
  };

  onBeforeAppend = (e) => {
    if (this.getTypeFilterCheckboxes().length === 0 || this.usesServerTypeFilter()) return;
    const selectedTypes = this.getSelectedTypes();
    const items = e.detail?.items || [];
    items.forEach((item) => {
      if (item.classList?.contains('product-wrapper')) {
        this.applyTypeVisibility(item, selectedTypes);
      }
    });
  };

  onInfiniteAppend = () => {
    if (this.getTypeFilterCheckboxes().length === 0) {
      this.runAnim();
      return;
    }
    this.filterByType();
  };

  updateClearTypeFiltersVisibility = () => {
    if (!this.clearTypeFiltersBtn) return;
    const anyChecked = Array.from(this.getTypeFilterCheckboxes()).some((cb) => cb.checked);
    this.clearTypeFiltersBtn.hidden = !anyChecked;
  };

  onClickClearTypeFilters = () => {
    this.getTypeFilterCheckboxes().forEach((cb) => (cb.checked = false));
    if (this.usesServerTypeFilter()) {
      this.applyFilter(this.typeFilterUrl(''));
      return;
    }
    this.filterByType();
  };

  // ────────────────────────────────────────────────────────────────────
  // Advanced filter modal
  // ────────────────────────────────────────────────────────────────────
  toggleFilterModal = () => {
    if (!this.filterModal) return;
    const filters = this.filterModal.querySelector('.filters');

    if (!this.modalOpen) {
      this.modalOpen = true;
      this.filterModal.classList.add('active');
      lockScroll();
      gsap.fromTo(filters, { opacity: 0, x: -40 }, { opacity: 1, x: 0, duration: 0.7, ease: 'power2.out', delay: 0.2 });
    } else {
      this.modalOpen = false;
      unlockScroll();
      gsap.to(filters, {
        opacity: 0,
        x: -40,
        duration: 0.4,
        ease: 'power2.out',
        onComplete: () => this.filterModal.classList.remove('active'),
      });
    }
  };

  onClickModalBackdrop = (e) => {
    // Only close when the click lands on the backdrop itself, not inside
    // the filters panel.
    if (e.target === this.filterModal && this.modalOpen) {
      this.toggleFilterModal();
    }
  };

  onKeydown = (e) => {
    if (e.key === 'Escape' && this.modalOpen) {
      this.toggleFilterModal();
    }
  };

  /**
   * Build a target URL from the modal's current input state. Strips any
   * existing `filter.*` params and rebuilds from the checked inputs and
   * filled price fields. Non-filter params (page, sort_by, etc.) are
   * preserved.
   */
  composeFilterUrl = () => {
    const url = new URL(window.location.href);

    Array.from(url.searchParams.keys()).forEach((key) => {
      if (key.startsWith('filter.')) url.searchParams.delete(key);
    });

    this.filterInputs.forEach((input) => {
      if (!input.checked) return;
      const name = input.getAttribute('name');
      const value = input.value;
      if (name && value !== '') url.searchParams.append(name, value);
    });

    this.priceInputs.forEach((input) => {
      const name = input.getAttribute('name');
      if (!name) return;
      if (input.value === '' || input.value == null) return;
      url.searchParams.set(name, input.value);
    });

    return collapseSingleValueParams(url.toString());
  };

  onClickApply = () => {
    const url = this.composeFilterUrl();
    if (this.modalOpen) this.toggleFilterModal();
    this.applyFilter(url);
  };

  // Auto-apply on input change. Keep the modal panel in place so it does
  // not flash closed/open — only the grid, count and chips update.
  onFilterInputChange = () => {
    const url = this.composeFilterUrl();
    this.applyFilter(url, { preserveModal: true });
  };

  onClickClearAllFilters = (e) => {
    const url = e.currentTarget?.dataset.filterUrlClear;
    if (url) this.applyFilter(url);
  };

  // Clear inside the modal only resets the inputs — apply happens on
  // pressing the Apply button.
  onClickClearAllFiltersLocal = () => {
    this.filterInputs.forEach((input) => (input.checked = false));
    this.priceInputs.forEach((input) => (input.value = ''));
  };

  // ────────────────────────────────────────────────────────────────────
  // Fetch + section-render swap
  // ────────────────────────────────────────────────────────────────────
  applyFilter = async (rawUrl, { preserveModal = false } = {}) => {
    if (!rawUrl) return;

    // A URL must never carry two product types — keep the first.
    rawUrl = collapseSingleValueParams(rawUrl);

    // Cancel any in-flight fetch
    if (this.filterFetchAbort) this.filterFetchAbort.abort();
    this.filterFetchAbort = new AbortController();

    this.filterModal?.classList.add('is-loading');

    const url = new URL(rawUrl, window.location.origin);
    const sectionUrl = new URL(url.toString());
    sectionUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(sectionUrl.toString(), {
        signal: this.filterFetchAbort.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // When preserving the modal (auto-apply from inside the open panel)
      // leave the modal DOM untouched so it does not flash — only the grid,
      // count and selected-filter chips update.
      if (!preserveModal) {
        this.swapRegion(doc, '[data-filter-modal]', this.filterModal);
      }
      this.swapRegion(doc, '[data-selected-filters]', this.selectedFiltersWrap);

      const newResultsCount = doc.querySelector('.results-count');
      if (newResultsCount && this.resultsCount) {
        this.resultsCount.textContent = newResultsCount.textContent;
      }

      this.updateFilterCount(doc);

      this.swapInfiniteScroll(doc);

      // Re-bind to the new DOM nodes. When the modal was preserved its
      // controls keep their existing listeners, so only rebind the swapped
      // chip strip to avoid duplicate handlers.
      this.refreshElementRefs();
      if (preserveModal) {
        this.bindSelectedFilters();
      } else {
        this.initFilterDropdowns();
        this.bindFilterControls();
      }
      this.collapseSelectedFiltersIfEmpty();
      this.restoreGridState();


      // Preserve open state if user was filtering inside the modal
      if (this.modalOpen && this.filterModal) {
        this.filterModal.classList.add('active');
      }

      window.history.pushState({}, '', url.toString());
      this.syncTypeChipsFromUrl(url.toString());

      // Notify listeners (e.g. filter cards) so they can re-sync their
      // own active state against the new URL.
      this.container.dispatchEvent(
        new CustomEvent('collection:filtered', { detail: { url: url.toString() } })
      );

      if (this.getTypeFilterCheckboxes().length > 0) {
        this.filterByType();
      } else {
        this.runAnim();
      }
      ScrollTrigger.refresh();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Filter apply failed:', error);
      }
    } finally {
      this.filterFetchAbort = null;
      this.filterModal?.classList.remove('is-loading');
    }
  };

  updateFilterCount = (doc) => {
    if (!this.filterByBtn) return;

    const incoming = doc.querySelector('button.filterby [data-filter-count]');
    let badge = this.filterCountEl || this.filterByBtn.querySelector('[data-filter-count]');

    if (!badge && incoming) {
      badge = incoming.cloneNode(true);
      this.filterByBtn.appendChild(badge);
    }
    if (!badge) return;

    const count = parseInt((incoming || badge).textContent, 10) || 0;
    badge.textContent = String(count);
    badge.hidden = count === 0;
    this.filterCountEl = badge;
  };

  swapRegion = (doc, selector, currentEl) => {
    if (!currentEl) return;
    const incoming = doc.querySelector(selector);
    if (incoming) currentEl.replaceWith(incoming);
  };

  swapInfiniteScroll = (doc) => {
    const currentWrap = this.container.querySelector('[data-infinite-scroll]');
    const incomingWrap = doc.querySelector('[data-infinite-scroll]');
    if (!currentWrap || !incomingWrap) return;
    currentWrap.replaceWith(incomingWrap);
    reinitInfiniteScroll(incomingWrap);
  };

  // ────────────────────────────────────────────────────────────────────
  // History
  // ────────────────────────────────────────────────────────────────────
  onPopState = () => {
    this.applyFilter(window.location.href);
  };
}
